import i18n from '@/utils/i18n';
import moment from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getGtinName } from '@/utils/gtin-metadata';
import {
  getMinAndMaxQuantity,
  useAdaptVariantPackToItemListOption,
} from './item-context-utils';
import { getParamsFromNameValue } from '@/utils/salt-item';
import {
  getVisibleQuantity,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { gql } from '@apollo/client';
import { GTINMetadata } from './item-context-types';
import { isMatchingFn, trackScanningEvent } from '@/utils/scanning';
import {
  ItemsContext_VariantsPackFieldsFragment,
  NewOrder_ScannerCartVariantPackQuery,
  NewOrder_ScannerCartVariantPackQueryVariables,
} from '@/types';
import {
  OffDeviceSearchReason,
  OnDeviceDataset,
  ScannerType,
  ScanningEventSource,
  ScanningSearchMethod,
  ScanSound,
} from '@/components/scanner/scanner-types';
import { OfflineSyncType } from '@/utils/offline-mode';
import { OnDeviceScanningSearchType } from './dynamic-config';
import { useAuthenticatedUserContext } from './user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGetAddToOrderDisabled } from '@/hooks/use-add-to-order-disabled';
import { useItemDisplayOptions } from './new-order-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useNewOrderQuantityStoreApi } from '@/stores/new-order-quantity-store';
import {
  useOfflineDataStatus,
  useOfflineVisibleVariantPacks,
} from '@/hooks/use-offline-data';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOrderGuideItemsContext } from './order-guide-items-context';
import { usePlayScanSounds } from '@/hooks/use-play-scan-sounds';
import { useScannerSearch } from '@/hooks/use-scanner-search';
import { useToasts } from './toast-context';
import { useVariantPackInfoContext } from './variant-pack-info-context';
import { VARIANTS_PACK_FRAGMENT } from './items-context-queries';

type AddedScanResults = Partial<
  Record<string, ItemsContext_VariantsPackFieldsFragment>
>;

export interface MultiUpdateQuantityMap {
  [variantPackUUID: string]: {
    quantity: number;
    variantPack: ItemsContext_VariantsPackFieldsFragment;
  };
}

export interface ScannerContextType {
  addToOrder: (variantPack: ItemsContext_VariantsPackFieldsFragment) => boolean;
  cartVariantPacks: ItemsContext_VariantsPackFieldsFragment[] | null;
  clearResults: () => void;
  errorText: string | null;
  isOffDeviceSearchInProgress: boolean;
  isOnDeviceSearchInProgress: boolean;
  newVariantPackUUIDs: Set<string> | null;
  offDeviceSearchResults: ItemsContext_VariantsPackFieldsFragment[] | null;
  onDeviceSearchResults: ItemsContext_VariantsPackFieldsFragment[] | null;
  removeFromOrder: (variantPackUUID: string) => void;
  searchAndUpdateOrderFn: (
    code: string,
    scannerType: ScannerType,
  ) => Promise<void>;
  setMultiVariantPackUpdates: React.Dispatch<
    React.SetStateAction<MultiUpdateQuantityMap | null>
  >;
  syncCartVariantPacks: () => void;
  updateCartWithMultiUpdates: () => void;
}

interface Props {
  children?: React.ReactNode;
}

export const SCANNER_CART_VARIANT_PACKS_QUERY = gql`
  query NewOrder_ScannerCartVariantPack(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
    $variantPackUUIDs: [uuid!]!
  ) {
    visible_variant_packs(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
        variant_pack_uuid: { _in: $variantPackUUIDs }
      }
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

const OFFLINE_CATALOG_STALE_HOURS = 24;

const getActiveVariantPackUUIDs = (
  quantityState: QuantityStateType,
): string[] =>
  Object.entries({
    ...quantityState.localQuantityMap,
    ...quantityState.savedQuantityMap,
  }).reduce<string[]>((acc, [uuid, quantity]) => {
    if (quantity > 0) {
      acc.push(uuid);
    }
    return acc;
  }, []);

export const ScannerContext = createContext<ScannerContextType | null>(null);

export const ScannerContextProvider: React.FC<Props> = ({ children }) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const isOfflineModeActive = useOfflineModeActive();
  const itemQueryVariables = useItemQueryVariables();
  const playScanSound = usePlayScanSounds();
  const getIsItemDisabled = useGetAddToOrderDisabled();
  const quantityStore = useNewOrderQuantityStoreApi();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { allVariantPacks } = useOrderGuideItemsContext();
  const { getVariantPackInfo } = useVariantPackInfoContext();
  const { gtinSettings } = useItemDisplayOptions();
  const { showErrorToast } = useToasts();

  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const onDeviceScanningConfig = useDynamicConfigValue(
    'on_device_scanning_config',
  );

  const { data: offlineDataStatus } = useOfflineDataStatus();
  const {
    clearResults: clearScanResults,
    isOffDeviceSearchInProgress,
    isOnDeviceSearchInProgress,
    offDeviceSearchResults,
    onDeviceSearchResults,
    searchOffDevice,
    searchOnDevice,
  } = useScannerSearch();

  const addedScanResultsRef = useRef<AddedScanResults>({});
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const codeRef = useRef<string>(null);

  const [newVariantPackUUIDs, setNewVariantPackUUIDs] =
    useState<Set<string> | null>(null);
  const [cartVariantPacks, setCartVariantPacks] = useState<
    ItemsContext_VariantsPackFieldsFragment[] | null
  >(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [multiVariantPackUpdates, setMultiVariantPackUpdates] =
    useState<MultiUpdateQuantityMap | null>(null);

  const scannerCartVariantPacksQuery = useLazyQuery<
    NewOrder_ScannerCartVariantPackQuery,
    NewOrder_ScannerCartVariantPackQueryVariables
  >(SCANNER_CART_VARIANT_PACKS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { data: offlineVisibleVariantPackData } = useOfflineVisibleVariantPacks(
    restaurantUUID,
    supplierUUID,
  );

  const offlineVisibleVariantPacks = useMemo(() => {
    return (offlineVisibleVariantPackData?.visible_variant_packs || []).map(
      (vp) => vp.variant_pack!,
    );
  }, [offlineVisibleVariantPackData]);

  const cartVariantPackUUIDs = useMemo(() => {
    return new Set((cartVariantPacks || []).map((vp) => vp.uuid));
  }, [cartVariantPacks]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (cartVariantPacks) {
      return;
    }

    const loadCartVariantPacks = async () => {
      const variantPackUUIDs = getActiveVariantPackUUIDs(
        quantityStore.getState().quantityState,
      );
      if (isOfflineModeActive) {
        if (!offlineVisibleVariantPackData) {
          return;
        }

        const variantPackUUIDSet = new Set(variantPackUUIDs);
        const cartVariantPacks = (
          offlineVisibleVariantPackData.visible_variant_packs || []
        ).reduce<ItemsContext_VariantsPackFieldsFragment[]>((acc, vvp) => {
          const variantPack = vvp.variant_pack;
          if (variantPack && variantPackUUIDSet.has(variantPack.uuid)) {
            acc.push(variantPack);
          }
          return acc;
        }, []);

        setCartVariantPacks(cartVariantPacks);
      } else {
        try {
          const variantPackUUIDSet = new Set(variantPackUUIDs);
          const { data } = await scannerCartVariantPacksQuery({
            variables: {
              ...itemQueryVariables,
              restaurantUUID,
              supplierUUID,
              variantPackUUIDs,
            },
          });
          const cartVariantPacks = (data.visible_variant_packs || []).reduce<
            ItemsContext_VariantsPackFieldsFragment[]
          >((acc, vvp) => {
            const variantPack = vvp.variant_pack!;
            if (variantPackUUIDSet.has(variantPack.uuid)) {
              acc.push(variantPack);
            }
            return acc;
          }, []);

          setCartVariantPacks(cartVariantPacks);
        } catch {
          setCartVariantPacks([]);
        }
      }
    };

    loadCartVariantPacks();
  }, [
    cartVariantPacks,
    isOfflineModeActive,
    itemQueryVariables,
    offlineVisibleVariantPackData,
    quantityStore,
    restaurantUUID,
    scannerCartVariantPacksQuery,
    supplierUUID,
  ]);

  const addToOrder = useCallback(
    (variantPack: ItemsContext_VariantsPackFieldsFragment) => {
      const variantPackUUID = variantPack.uuid;
      const quantity = getVisibleQuantity(
        quantityStore.getState().quantityState,
        variantPackUUID,
      );
      const variantPackInfo = getVariantPackInfo(variantPackUUID);
      const { maximumQuantity, minimumQuantity } =
        getMinAndMaxQuantity(variantPackInfo);
      const isAvailable = !getIsItemDisabled(variantPackUUID);

      let newQuantity = quantity + 1;
      let scanSound =
        newQuantity === 1
          ? ScanSound.IncrementFirst
          : ScanSound.IncrementSubsequent;

      if (!isAvailable) {
        scanSound = ScanSound.Unavailable;
      } else if (
        typeof minimumQuantity === 'number' &&
        newQuantity < minimumQuantity
      ) {
        newQuantity = minimumQuantity;
      } else if (
        typeof maximumQuantity === 'number' &&
        newQuantity >= maximumQuantity
      ) {
        newQuantity = maximumQuantity;
        scanSound = ScanSound.MaxQuantity;
      }

      const restaurantOverrides = variantPack.item.restaurant_item_overrides[0];
      let displayName =
        restaurantOverrides?.display_name || variantPack.item.display_name;
      // Use SALT information if enabled, otherwise use GTIN metadata if available
      if (saltApiIntegrationEnabled) {
        if (variantPack.salt) {
          displayName =
            getParamsFromNameValue(variantPack.salt.basicInformation.names)
              .Name ?? displayName;
        }
      } else if (gtinSettings) {
        const gtinMetadata = (variantPack.gtin_metadata?.[0]?.metadata ??
          null) as GTINMetadata | null;
        if (gtinMetadata) {
          displayName = getGtinName(gtinSettings, gtinMetadata, displayName);
        }
      }

      playScanSound(scanSound);

      // Add scanned item to top of the list if not already there
      setCartVariantPacks((prev) => {
        if (!prev) {
          return null;
        }

        if (prev[0]?.uuid === variantPackUUID) {
          return prev;
        }

        return cartVariantPackUUIDs.has(variantPackUUID)
          ? [variantPack, ...prev.filter((vp) => vp.uuid !== variantPack.uuid)]
          : [variantPack, ...prev];
      });

      if (!isAvailable) {
        showErrorToast(i18n.t('item_is_unavailable'));
      } else if (quantity !== newQuantity) {
        debouncedUpdateQuantity(
          {
            eventPayloads: null,
            newQuantity,
            previousQuantity: quantity,
            variantPackDetails: {
              itemDisplayName: displayName,
              metadata: variantPack.metadata,
            },
            variantPackUUID,
          },
          { source: 'scanner' },
        );
        return true;
      }

      return false;
    },
    [
      cartVariantPackUUIDs,
      debouncedUpdateQuantity,
      getVariantPackInfo,
      getIsItemDisabled,
      gtinSettings,
      playScanSound,
      quantityStore,
      saltApiIntegrationEnabled,
      showErrorToast,
    ],
  );

  const highlightNewVariantPacks = useCallback(
    (variantPackUUIDs: Set<string>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setNewVariantPackUUIDs(variantPackUUIDs);
      timeoutRef.current = setTimeout(() => {
        setNewVariantPackUUIDs(null);
      }, 1000);
    },
    [],
  );

  const handleAutoAddToOrder = useCallback(
    (
      code: string,
      variantPackToAddToOrder: ItemsContext_VariantsPackFieldsFragment,
    ) => {
      const addedToOrder = addToOrder(variantPackToAddToOrder);
      if (addedToOrder) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        highlightNewVariantPacks(new Set([variantPackToAddToOrder.uuid]));
        // Only cache successful adds so a failed one (e.g. unavailable pack)
        // re-runs the search on rescan instead of replaying the failure.
        addedScanResultsRef.current[code] = variantPackToAddToOrder;
      }
    },
    [addToOrder, highlightNewVariantPacks],
  );

  const updateCartWithMultiUpdates = useCallback(() => {
    clearScanResults();

    if (!multiVariantPackUpdates) {
      return;
    }

    const { updatedVariantPacks, variantPackUUIDsToRemove } = Object.entries(
      multiVariantPackUpdates,
    ).reduce<{
      updatedVariantPacks: ItemsContext_VariantsPackFieldsFragment[];
      variantPackUUIDsToRemove: Set<string>;
    }>(
      (acc, [variantPackUUID, { quantity, variantPack }]) => {
        if (quantity === 0) {
          acc.variantPackUUIDsToRemove.add(variantPackUUID);
        } else {
          acc.updatedVariantPacks.push(variantPack);
        }

        return acc;
      },
      { updatedVariantPacks: [], variantPackUUIDsToRemove: new Set() },
    );
    const updatedVariantPackUUIDs = new Set(
      updatedVariantPacks.map((vp) => vp.uuid),
    );

    setCartVariantPacks((prev) => {
      if (!prev) {
        return null;
      }

      return [
        ...updatedVariantPacks,
        ...prev.filter(
          (vp) =>
            !variantPackUUIDsToRemove.has(vp.uuid) &&
            !updatedVariantPackUUIDs.has(vp.uuid),
        ),
      ];
    });
    highlightNewVariantPacks(updatedVariantPackUUIDs);
    setMultiVariantPackUpdates(null);
  }, [clearScanResults, highlightNewVariantPacks, multiVariantPackUpdates]);

  const searchAndUpdateOrderFn = useCallback(
    async (code: string, scannerType: ScannerType) => {
      codeRef.current = code;
      setErrorText(null);

      if (!code) {
        clearScanResults();
        return;
      }

      updateCartWithMultiUpdates();
      const dataStatus = offlineDataStatus?.[restaurantUUID];
      const lastCatalogDownloadDate =
        dataStatus && OfflineSyncType.VisibleVariantPacks in dataStatus
          ? dataStatus[OfflineSyncType.VisibleVariantPacks]!.downloadedDate
          : null;
      const offlineCatalogAgeHours = lastCatalogDownloadDate
        ? moment().diff(moment(lastCatalogDownloadDate), 'hours')
        : null;
      const isOfflineDataStale =
        offlineCatalogAgeHours === null ||
        offlineCatalogAgeHours >= OFFLINE_CATALOG_STALE_HOURS;
      const useOfflineDataset = isOfflineModeActive || !isOfflineDataStale;

      const commonScanEventProperties = {
        isOfflineCatalogStale: isOfflineDataStale,
        isOfflineModeActive,
        offlineCatalogAgeHours,
        onDeviceDataset: useOfflineDataset
          ? OnDeviceDataset.OfflineCatalog
          : OnDeviceDataset.OrderGuide,
        restaurantUUID,
        source: ScanningEventSource.Order,
        supplierUUID,
        type: scannerType,
      };

      const previouslyScannedVariantPack = addedScanResultsRef.current[code];
      if (previouslyScannedVariantPack) {
        trackScanningEvent({
          ...commonScanEventProperties,
          code,
          rawResults: [previouslyScannedVariantPack.uuid],
          searchMethod: ScanningSearchMethod.PreviousScanned,
        });
        handleAutoAddToOrder(code, previouslyScannedVariantPack);
        return;
      }

      // Use offline data when in offline mode and also opportunistically
      // when online but offline data is available and not stale
      const onDeviceSearchResults = searchOnDevice(
        code,
        useOfflineDataset ? offlineVisibleVariantPacks : allVariantPacks,
        // Skip scan sound playback when online since off device search will play scan sounds
        !isOfflineModeActive,
      );

      if (onDeviceSearchResults.length > 0) {
        trackScanningEvent({
          ...commonScanEventProperties,
          code,
          rawResults: onDeviceSearchResults.map((vp) => vp.uuid),
          searchMethod: ScanningSearchMethod.OnDevice,
        });

        if (onDeviceSearchResults.length === 1) {
          const onDeviceSingleMatch = onDeviceSearchResults[0];
          const onDeviceSingleMatchOption =
            adaptVariantPackToItemListOption(onDeviceSingleMatch);

          // Only auto add matches on code and not display name
          const { codeMatch } = isMatchingFn(
            code,
            onDeviceScanningConfig?.search_type ??
              OnDeviceScanningSearchType.EXACT_MATCH,
            onDeviceSingleMatchOption,
          );

          if (codeMatch) {
            handleAutoAddToOrder(code, onDeviceSingleMatch);
            return;
          }
        }
      }
      // Apollo doesn't provide a mechanism to cancel an in-flight query so
      // this ensures that results from the latest query are displayed
      if (codeRef.current === code && !isOfflineModeActive) {
        let offDeviceSearchReason: OffDeviceSearchReason;
        if (onDeviceSearchResults.length === 0) {
          offDeviceSearchReason = OffDeviceSearchReason.NoOnDeviceMatch;
        } else if (onDeviceSearchResults.length === 1) {
          offDeviceSearchReason = OffDeviceSearchReason.NameOnlyOnDeviceMatch;
        } else {
          offDeviceSearchReason = OffDeviceSearchReason.AmbiguousOnDeviceMatch;
        }
        const offDeviceSearchResults = await searchOffDevice(code);
        trackScanningEvent({
          ...commonScanEventProperties,
          code,
          offDeviceSearchReason,
          rawResults: offDeviceSearchResults.map((vp) => vp.uuid),
          searchMethod: ScanningSearchMethod.OffDevice,
        });

        if (offDeviceSearchResults.length === 1) {
          handleAutoAddToOrder(code, offDeviceSearchResults[0]);
        } else if (offDeviceSearchResults.length === 0) {
          setErrorText(i18n.t('scan.no_results'));
        }
      } else if (onDeviceSearchResults.length === 0 && isOfflineModeActive) {
        playScanSound(ScanSound.NoResults);
        setErrorText(i18n.t('scan.no_results'));
      }
    },
    [
      adaptVariantPackToItemListOption,
      allVariantPacks,
      clearScanResults,
      handleAutoAddToOrder,
      isOfflineModeActive,
      offlineDataStatus,
      offlineVisibleVariantPacks,
      onDeviceScanningConfig,
      playScanSound,
      restaurantUUID,
      searchOffDevice,
      searchOnDevice,
      supplierUUID,
      updateCartWithMultiUpdates,
    ],
  );

  const clearResults = useCallback(() => {
    clearScanResults();
    setErrorText(null);
  }, [clearScanResults, setErrorText]);

  const removeFromOrder = useCallback(
    (variantPackUUID: string) => {
      setCartVariantPacks(
        (prev) => prev?.filter((vp) => vp.uuid !== variantPackUUID) ?? null,
      );
      clearScanResults();
    },
    [clearScanResults],
  );

  const syncCartVariantPacks = useCallback(() => {
    const activeUUIDs = getActiveVariantPackUUIDs(
      quantityStore.getState().quantityState,
    );
    setCartVariantPacks((cvp) => {
      if (cvp) {
        return cvp.filter((vp) => activeUUIDs.includes(vp.uuid));
      }

      return cvp;
    });
  }, [quantityStore]);

  const contextValue = useMemo(
    () => ({
      addToOrder,
      cartVariantPacks,
      clearResults,
      errorText,
      isOffDeviceSearchInProgress,
      isOnDeviceSearchInProgress,
      newVariantPackUUIDs,
      offDeviceSearchResults,
      onDeviceSearchResults,
      removeFromOrder,
      searchAndUpdateOrderFn,
      setMultiVariantPackUpdates,
      syncCartVariantPacks,
      updateCartWithMultiUpdates,
    }),
    [
      addToOrder,
      cartVariantPacks,
      clearResults,
      errorText,
      isOffDeviceSearchInProgress,
      isOnDeviceSearchInProgress,
      newVariantPackUUIDs,
      offDeviceSearchResults,
      onDeviceSearchResults,
      removeFromOrder,
      searchAndUpdateOrderFn,
      syncCartVariantPacks,
      updateCartWithMultiUpdates,
    ],
  );

  return (
    <ScannerContext.Provider value={contextValue}>
      {children}
    </ScannerContext.Provider>
  );
};

export const useScannerContext = (): ScannerContextType => {
  const context = useContext(ScannerContext);
  if (!context) {
    throw new Error(
      'useScannerContext must be used within a ScannerContextProvider',
    );
  }
  return context;
};
